import useSWR, { mutate } from "swr";
import { fDateTime, fetcher, axiosInstance } from "../../../utils";
import {
  ButtonBase,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useDebouncedValue } from "../../../hooks";
import { useSearchParams } from "react-router-dom";
import { Iconify } from "../../../components";
import { useEffect, useState } from "react";
import { saveAs } from "file-saver";
import XLSX from "xlsx-js-style";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { enqueueSnackbar } from "notistack";

enum TypeEnum {
  Pre = 0,
  Tts = 1,
}
const TableLableStyle = {
  fontSize: "16px",
  lineHeight: "24px",
  fontWeight: 500,
  color: "#3D3D3D",
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.grey[100],
  },
}));

const mapTtsStatus = (status: number) => {
  switch (status) {
    case 0:
      return "Görüşülüyor";
    case 1:
      return "Tamamlandı";
    case 2:
      return "Reddedildi";
    default:
      return "-";
  }
};
export const ProspectListSection = () => {
  const [searchParams] = useSearchParams();
  const [debouncedSearch, search, setSearch] = useDebouncedValue("");
  const [filter, setFilter] = useState("");
  const [ttsFilter, setTtsFilter] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [statusMap, setStatusMap] = useState<Record<string, number>>({});

  const [type, setType] = useState(1); // pre: 0, tts: 1

  useEffect(() => {
    if (searchParams.get("search")) {
      setSearch(searchParams.get("search")!);
    }
  }, [searchParams.get("search")]);

  const params = new URLSearchParams();
  if (debouncedSearch) params.append("search", debouncedSearch);

  const listKey =
    type === TypeEnum.Pre
      ? `/montage/pre?${params.toString()}`
      : `/ttsform?${params.toString()}`;
  let { data, isLoading } = useSWR<any[]>(listKey, fetcher);

  if (data && type === TypeEnum.Tts) {
    data = data.filter((item) => item.status === Number(ttsFilter));
  }

  const exportToExcel = () => {
    let header = [];
    let rows = [];

    if (type === TypeEnum.Pre) {
      header = [
        "Firma Adı",
        "Yetkili Kişi E-mail",
        "Yetkili Kişi Telefon",
        "Araç Tipi",
        "Başvuru Tarihi",
      ];

      rows = [
        header,
        ...(data ?? []).map((item) => [
          item.company_name ?? "",
          item.authorized_person_email ?? "",
          item.authorized_person_phone ?? "",
          item.vehicles
            ?.map((v: any) => `${v.quantity} ${v.vehicle_type}`)
            .join(", "),
          item.createdAt ? fDateTime(item.createdAt) : "-",
        ]),
      ];
    } else {
      header = [
        "Adı Soyadı",
        "Firma Adı",
        "Telefon",
        "Ürünler",
        "Araç Sayısı",
        "Oluşturulma Tarihi",
      ];

      rows = [
        header,
        ...(data ?? []).map((item) => [
          item.name ?? "-",
          item.company_name ?? "-",
          item.phone ?? "-",
          item.products?.length
            ? item.products.map((p: any) => p).join(",")
            : "-",
          item.vehicle_quantity ?? "-",
          item.createdAt ? fDateTime(item.createdAt) : "-",
        ]),
      ];
    }

    const ws = XLSX.utils.aoa_to_sheet(rows);

    ws["!cols"] = header.map(() => ({ wch: 30, hch: 10 }));
    ws["!rows"] = [{ hpx: 30 }];

    const headerRange = XLSX.utils.decode_range(ws["!ref"] as string);

    for (let C = headerRange.s.c; C <= headerRange.e.c; C++) {
      let cellRef = XLSX.utils.encode_cell({ r: 0, c: C });
      if (ws[cellRef]) {
        ws[cellRef].s = {
          font: { bold: true },
          alignment: { vertical: "center", horizontal: "center" },
        };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

    saveAs(
      blob,
      type === TypeEnum.Pre
        ? `Montaj-Basvuruları-IlkAdim-${new Date().valueOf()}.xlsx`
        : `TTS-Basvurulari-${new Date().valueOf()}.xlsx`
    );
  };

  // Optimistic update handler: updates local UI, sends PATCH to server and updates SWR cache
  const handleStatusChange = async (
    id: string | number | null,
    newStatus: number
  ) => {
    // Always use string key for statusMap
    const key = String(id);
    setStatusMap((prev) => ({ ...prev, [key]: newStatus }));
    setEditingId(null);

    if (!id) {
      console.warn("No id available for updating status");
      return;
    }

    try {
      await axiosInstance.patch(`/ttsform/${id}/status`, { status: newStatus });
      mutate(
        listKey,
        (current: any[] | undefined) => {
          if (!current) return current;
          return current.map((item) => {
            const itemId = String(item.id ?? item._id);
            if (itemId === key) {
              return { ...item, status: newStatus };
            }
            return item;
          });
        },
        false
      );
      enqueueSnackbar("Durum başarıyla güncellendi.", { variant: "success" });
    } catch (err) {
      console.error("Failed to update status", err);
      setStatusMap((prev) => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    }
  };

  return (
    <Stack gap={2}>
      <Stack
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        direction="row"
      >
        <Stack direction="row" gap={1}>
          <TextField
            placeholder="Ara"
            sx={{ maxWidth: 350 }}
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
        </Stack>

        {/* Right-side controls grouped so selects stay close to each other */}
        <Stack direction="row" alignItems="center" gap={1}>
          {filter && (
            <IconButton
              onClick={() => {
                setFilter("");
              }}
            >
              <Iconify icon="ic:round-clear" />
            </IconButton>
          )}
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel id="type-filter">Form Tipi</InputLabel>
            <Select
              labelId="type-filter"
              id="type-filter"
              label="Tip Seç"
              onChange={(e) => {
                setType(e.target.value as number);
              }}
              value={type}
            >
              <MenuItem value={0}>Montaj</MenuItem>
              <MenuItem value={1}>TTS</MenuItem>
            </Select>
          </FormControl>

          {type === TypeEnum.Tts && (
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id="status-tts-filter">Durum</InputLabel>
              <Select
                labelId="status-tts-filter"
                id="status-tts-filter"
                label="TTS Durum Seç"
                onChange={(e) => {
                  setTtsFilter(e.target.value as number);
                }}
                value={ttsFilter}
              >
                <MenuItem value={0}>Görüşülüyor</MenuItem>
                <MenuItem value={1}>Tamamlandı</MenuItem>
                <MenuItem value={2}>Reddedildi</MenuItem>
              </Select>
            </FormControl>
          )}
        </Stack>
      </Stack>
      <TableContainer sx={{ maxHeight: "70vh", overflow: "auto" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.200" }}>
              <TableCell
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "grey.200",
                  zIndex: 2,
                }}
              >
                <Typography sx={TableLableStyle}>Başvuru Tarihi</Typography>
              </TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "grey.200",
                  zIndex: 2,
                }}
              >
                <Typography sx={TableLableStyle}>Firma Adı</Typography>
              </TableCell>
              {type === TypeEnum.Tts && (
                <TableCell
                  sx={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "grey.200",
                    zIndex: 2,
                  }}
                >
                  <Typography sx={TableLableStyle}>Adı Soyadı</Typography>
                </TableCell>
              )}
              {type === TypeEnum.Pre && (
                <TableCell
                  sx={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "grey.200",
                    zIndex: 2,
                  }}
                >
                  <Typography sx={TableLableStyle}>E-Posta</Typography>
                </TableCell>
              )}
              <TableCell
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "grey.200",
                  zIndex: 2,
                }}
              >
                <Typography sx={TableLableStyle}>Telefon</Typography>
              </TableCell>
              <TableCell
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "grey.200",
                  zIndex: 2,
                }}
              >
                <Typography sx={TableLableStyle}>
                  {type === TypeEnum.Pre ? "Araç" : "Araç Sayısı"}
                </Typography>
              </TableCell>
              {type === TypeEnum.Tts && (
                <>
                  <TableCell
                    sx={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "grey.200",
                      zIndex: 2,
                    }}
                  >
                    <Typography sx={TableLableStyle}>Ürünler</Typography>
                  </TableCell>

                  <TableCell
                    sx={{
                      position: "sticky",
                      top: 0,
                      backgroundColor: "grey.200",
                      zIndex: 2,
                    }}
                  >
                    <Typography sx={TableLableStyle}>Durum</Typography>
                  </TableCell>
                </>
              )}
              <TableCell
                align="right"
                sx={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "grey.200",
                  zIndex: 2,
                }}
              >
                {data?.length ? (
                  <ButtonBase
                    onClick={() => {
                      exportToExcel();
                    }}
                    sx={{
                      px: 1,
                      borderRadius: 2,
                      fontFamily: "inherit",
                      gap: 1,
                    }}
                  >
                    Tümünü İndir
                    <Iconify icon="vscode-icons:file-type-excel" />
                  </ButtonBase>
                ) : (
                  ""
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isLoading && !data?.length && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography sx={{ textAlign: "center", opacity: 0.75 }}>
                    Veri Bulunamadı
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {isLoading && !data?.length && (
              <>
                {Array(5)
                  .fill("")
                  .map((row, index) => (
                    <StyledTableRow key={index}>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      <TableCell>
                        <Skeleton />
                      </TableCell>
                      {type === TypeEnum.Tts && (
                        <>
                          <TableCell>
                            <Skeleton />
                          </TableCell>
                          <TableCell>
                            <Skeleton />
                          </TableCell>
                        </>
                      )}
                    </StyledTableRow>
                  ))}
              </>
            )}
            {/* Montaj - Pre */}
            {type == TypeEnum.Pre ? (
              data?.map((row) => (
                <StyledTableRow>
                  <TableCell>
                    <Typography sx={{}}>
                      {row.createdAt ? fDateTime(row.createdAt) : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={TableLableStyle}>
                      {row.company_name ?? "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={TableLableStyle}>
                      {row.authorized_person_email ?? "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={TableLableStyle}>
                      {row.authorized_person_phone ?? ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={TableLableStyle}>
                      {row.vehicles
                        ?.map((v: any) => `${v.quantity} ${v.vehicle_type}`)
                        .join(", ")}
                    </Typography>
                  </TableCell>

                  <TableCell align="right"></TableCell>
                </StyledTableRow>
              ))
            ) : type === TypeEnum.Tts ? (
              data?.map((row) => {
                const rowId = row.id ?? row._id;
                return (
                  <StyledTableRow key={rowId}>
                    <TableCell>
                      <Typography sx={{}}>
                        {row.createdAt ? fDateTime(row.createdAt) : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={TableLableStyle}>
                        {row.company_name ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={TableLableStyle}>
                        {row.name ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={TableLableStyle}>
                        {row.phone ?? ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={TableLableStyle}>
                        {row.vehicle_quantity ?? ""}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={TableLableStyle}>
                        {(row.products ?? [])
                          .map((prod: any) => prod)
                          .join(",")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" gap={1}>
                        {editingId === rowId ? (
                          <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel id={`edit-status-label-${rowId}`}>
                              Durum
                            </InputLabel>
                            <Select
                              labelId={`edit-status-label-${rowId}`}
                              value={statusMap[rowId] ?? row.status}
                              label="Durum"
                              size="small"
                              onChange={(e) => {
                                const newStatus = Number(e.target.value);
                                handleStatusChange(rowId, newStatus);
                              }}
                            >
                              <MenuItem value={0}>Görüşülüyor</MenuItem>
                              <MenuItem value={1}>Tamamlandı</MenuItem>
                              <MenuItem value={2}>Reddedildi</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip
                            size="small"
                            label={mapTtsStatus(statusMap[rowId] ?? row.status)}
                            color={
                              (statusMap[rowId] ?? row.status) === 0
                                ? "warning"
                                : (statusMap[rowId] ?? row.status) === 1
                                ? "success"
                                : "error"
                            }
                          />
                        )}
                        {editingId === rowId ? (
                          <VisibilityOffIcon
                            sx={{
                              fontSize: 18,
                              color: "#fb8c00",
                              cursor: "pointer",
                            }}
                            onClick={() => setEditingId(null)}
                          />
                        ) : (
                          <VisibilityIcon
                            sx={{
                              fontSize: 18,
                              color: "#fb8c00",
                              cursor: "pointer",
                            }}
                            onClick={() => setEditingId(rowId)}
                          />
                        )}
                      </Stack>
                    </TableCell>

                    <TableCell align="right"></TableCell>
                  </StyledTableRow>
                );
              })
            ) : (
              <></>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
